<template>
  <div>
    <async-items-loader :task="task" :num-items="visiblePosts.length">
      <app-user v-slot="{ isCustomer }">
        <span v-if="isCustomer">No posts found</span>
        <div v-else class="flex items-center">
          Connect your social media accounts to get started.
          <social-accounts-btn
            v-if="clientId"
            :client-id="clientId"
            icon="fa fa-plus"
            label="Connect"
            color="primary"
            class="on-right"
          />
        </div>
      </app-user>
    </async-items-loader>

    <slot
      v-if="paginationTop"
      name="pagination"
      v-bind="{ page, total, perPage, numPages, changePage, prev, next, isLoading }"
    >
      <div
        v-show="!persist && total" class="q-pa-sm flex flex-center bg-white q-mb-md"
      >
        <pagination
          :model-value="page"
          :total="total"
          :per-page="perPage"
          direction-links
          boundary-links
          class="bg-white"
          @update:model-value="changePage($event)"
        />
      </div>
    </slot>

    <slot v-bind="{ visiblePosts }">
      <posts-grid :posts="visiblePosts">
        <template v-slot="post">
          <post
            :post="post"
            :show-client="!isClientContext"
            notes
            trash
            actions
            scrollable
            popup
            class="animated fadeIn"
          />
        </template>
      </posts-grid>
    </slot>

    <loader v-if="infiniteScroll && page > 1" :task="task" class="q-my-md" />

    <slot
      v-if="!paginationTop"
      name="pagination"
      v-bind="{ page, total, perPage, numPages, changePage, prev, next, isLoading }"
    >
      <div
        v-show="!paginationTop && !persist && total"
        class="q-pa-sm flex flex-center bg-white q-mt-md"
      >
        <pagination
          :model-value="page"
          :total="total"
          :per-page="perPage"
          direction-links
          boundary-links
          class="bg-white"
          @update:modelValue="changePage($event)"
        />
      </div>
    </slot>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import _ from 'lodash';
import AsyncItemsLoader from '@/components/item/AsyncItemsLoader.vue';
import Loader from '@/components/loading/AsyncLinearProgress.vue';
import Pagination from '@/components/pagination/Pagination.vue';
import AppUser from '@/auth/AppUser.vue';
import SocialAccountsBtn from '@/social/account/AccountsBtn.vue';
import { isAtBottom } from '@/dom';
import PostsMixin from './PostsMixin';
import PostsGrid from './PostsGrid.vue';
import Post from '../card/Post.vue';

interface Data {
  scrollTargetElem: Element|Window|null,
}

export default defineComponent({
  mixins: [PostsMixin],
  components: {
    SocialAccountsBtn,
    AppUser,
    AsyncItemsLoader,
    Loader,
    PostsGrid,
    Post,
    Pagination,
  },
  props: {
    newPageTop: {
      type: Boolean,
      default: false,
    },
    infiniteScroll: {
      type: Boolean,
      default: false,
    },
    scrollTarget: String,
    paginationTop: {
      type: Boolean,
      default: false,
    },
  },
  data(): Data {
    return {
      scrollTargetElem: null,
    };
  },
  watch: {
    page() {
      if (this.newPageTop && window.scroll) {
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
    },
    infiniteScroll() {
      this.resetLoad();
    },
  },
  methods: {
    setupInfiniteScroll() {
      if (!this.infiniteScroll) return;
      this.scrollTargetElem = window;
      if (this.scrollTarget) {
        this.scrollTargetElem = this.$el.closest(this.scrollTarget);
      }
      if (!this.scrollTargetElem) return;
      this.scrollTargetElem.addEventListener('scroll', _.debounce(() => {
        if (!this.infiniteScroll || !this.scrollTargetElem) return;
        if (isAtBottom(this.scrollTargetElem, 500)) {
          this.next();
        }
      }, 250));
    },
  },
  mounted() {
    this.setupInfiniteScroll();
  },
});
</script>
